import { useState, useEffect, useRef } from "react";

const PRIMARY   = "#2b1dc9";
const P_HOVER   = "#2318a8";
const P_LIGHT   = "rgba(43,29,201,.09)";
const P_GLOW    = "rgba(43,29,201,.25)";
const BG        = "#f6f6f8";
const SURFACE   = "#ffffff";
const BORDER    = "#e4e4ec";
const TEXT      = "#0f0e1a";
const TEXT2     = "#4a4a6a";
const TEXT3     = "#8888aa";
const GREEN     = "#16a34a";
const AMBER     = "#d97706";
const RED       = "#dc2626";
const DARK      = "#0e0c1a";
const DARK3     = "#1a1830";
const SHADOW_SM = "0 1px 3px rgba(0,0,0,.06),0 1px 2px rgba(0,0,0,.04)";
const SHADOW_MD = "0 4px 16px rgba(0,0,0,.08),0 2px 4px rgba(0,0,0,.04)";
const SHADOW_LG = "0 12px 40px rgba(0,0,0,.10),0 4px 8px rgba(0,0,0,.05)";
const FONT      = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";

const styles = `
  /* Fonts loaded from index.html */
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body{font-family:${FONT};background:${BG};color:${TEXT};-webkit-font-smoothing:antialiased;line-height:1.6}
  .material-symbols-outlined{font-variation-settings:'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 24;user-select:none;line-height:1;display:inline-block;vertical-align:middle}

  @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes floatSlow{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
  @keyframes floatMed{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
  @keyframes gradMove{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes carouselScroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.4)}}
  @keyframes bubble{0%,100%{transform:translateY(0) scale(1);opacity:.7}50%{transform:translateY(-10px) scale(1.08);opacity:1}}

  .fade-up{animation:fadeUp .6s cubic-bezier(.22,1,.36,1) both}
  .fade-in{animation:fadeIn .5s ease both}
  .delay-1{animation-delay:.1s}.delay-2{animation-delay:.22s}
  .delay-3{animation-delay:.36s}.delay-4{animation-delay:.52s}.delay-5{animation-delay:.68s}

  .scroll-reveal{opacity:0;transform:translateY(22px);transition:opacity .6s cubic-bezier(.22,1,.36,1),transform .6s cubic-bezier(.22,1,.36,1)}
  .scroll-reveal.visible{opacity:1;transform:none}

  .s-nav-link{font-size:13px;font-weight:500;color:${TEXT2};text-decoration:none;background:none;border:none;cursor:pointer;font-family:${FONT};padding:7px 14px;border-radius:8px;transition:all .15s}
  .s-nav-link:hover,.s-nav-link.active{background:${P_LIGHT};color:${PRIMARY}}

  .s-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border:none;cursor:pointer;font-family:${FONT};font-weight:700;letter-spacing:.01em;transition:all .15s;text-decoration:none;white-space:nowrap}
  .s-btn:active{transform:scale(.97)}
  .s-btn-primary{background:${PRIMARY};color:#fff;box-shadow:0 4px 16px ${P_GLOW};border-radius:10px;padding:10px 22px;font-size:14px}
  .s-btn-primary:hover{background:${P_HOVER};transform:translateY(-1px);box-shadow:0 6px 24px ${P_GLOW}}
  .s-btn-ghost{background:transparent;color:${TEXT2};border:1.5px solid ${BORDER};border-radius:10px;padding:9px 20px;font-size:13px}
  .s-btn-ghost:hover{border-color:${PRIMARY};color:${PRIMARY}}
  .s-btn-white{background:#fff;color:${PRIMARY};border-radius:10px;padding:12px 28px;font-size:14px;box-shadow:0 6px 20px rgba(0,0,0,.12)}
  .s-btn-white:hover{transform:translateY(-1px);box-shadow:0 10px 28px rgba(0,0,0,.16)}
  .s-btn-outline-white{background:rgba(255,255,255,.1);color:#fff;border:1.5px solid rgba(255,255,255,.3);border-radius:10px;padding:11px 24px;font-size:13px}
  .s-btn-outline-white:hover{background:rgba(255,255,255,.18)}
  .s-btn-green{background:#16a34a;color:#fff;border-radius:10px;padding:11px 24px;font-size:14px;box-shadow:0 4px 14px rgba(22,163,74,.3)}
  .s-btn-green:hover{background:#15803d;transform:translateY(-1px)}

  .s-card{background:${SURFACE};border:1px solid ${BORDER};border-radius:14px;padding:28px;box-shadow:${SHADOW_SM};transition:all .2s;position:relative;overflow:hidden}
  .s-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:${PRIMARY};transform:scaleX(0);transform-origin:left;transition:transform .28s cubic-bezier(.22,1,.36,1);border-radius:14px 14px 0 0}
  .s-card:hover{box-shadow:${SHADOW_LG};transform:translateY(-3px);border-color:rgba(43,29,201,.18)}
  .s-card:hover::before{transform:scaleX(1)}

  .sec-tag{display:inline-flex;align-items:center;gap:7px;padding:5px 13px;border-radius:20px;background:${P_LIGHT};color:${PRIMARY};font-size:11px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;border:1px solid rgba(43,29,201,.12)}

  .app-win{background:#1e2a22;border-radius:14px;overflow:hidden;box-shadow:0 32px 80px rgba(0,0,0,.35),0 0 0 1px rgba(255,255,255,.06)}
  .app-bar{background:#131c16;padding:11px 16px;display:flex;align-items:center;gap:7px;border-bottom:1px solid rgba(255,255,255,.06)}
  .app-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}

  .sec-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:24px;transition:all .22s}
  .sec-card:hover{background:rgba(255,255,255,.07);border-color:rgba(43,29,201,.35);transform:translateY(-3px)}

  .f-tab{padding:9px 18px;border-radius:30px;border:none;cursor:pointer;font-family:${FONT};font-size:13px;font-weight:500;transition:all .18s}
  .f-tab-active{background:${PRIMARY};color:#fff;box-shadow:0 3px 10px ${P_GLOW}}
  .f-tab-inactive{background:transparent;color:${TEXT3}}
  .f-tab-inactive:hover{color:${TEXT};background:${BG}}

  .stat-num{font-size:clamp(40px,5vw,64px);font-weight:900;line-height:1;letter-spacing:-.03em}

  .carousel-wrap{overflow:hidden;position:relative}
  .carousel-wrap::before,.carousel-wrap::after{content:'';position:absolute;top:0;bottom:0;width:100px;z-index:2;pointer-events:none}
  .carousel-wrap::before{left:0;background:linear-gradient(to right,${SURFACE},transparent)}
  .carousel-wrap::after{right:0;background:linear-gradient(to left,${SURFACE},transparent)}
  .carousel-track{display:flex;gap:18px;animation:carouselScroll 40s linear infinite;width:max-content;padding:16px 0}
  .carousel-track:hover{animation-play-state:paused}
  .c-card{flex-shrink:0;width:300px;background:${BG};border:1px solid ${BORDER};border-radius:18px;padding:24px;transition:all .2s;cursor:default}
  .c-card:hover{background:${SURFACE};border-color:rgba(43,29,201,.2);box-shadow:${SHADOW_LG};transform:translateY(-4px)}
  .c-icon{width:44px;height:44px;background:${P_LIGHT};border-radius:11px;display:flex;align-items:center;justify-content:center;margin-bottom:16px;transition:all .2s}
  .c-card:hover .c-icon{background:${PRIMARY}}
  .c-card:hover .c-icon .material-symbols-outlined{color:#fff!important}

  .grad-text{background:linear-gradient(135deg,${PRIMARY},#7c3aed,#2563eb);background-clip:text;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-size:200% 200%;animation:gradMove 4s ease infinite}

  .trust-badge{display:inline-flex;align-items:center;gap:7px;padding:7px 14px;border-radius:30px;background:${P_LIGHT};border:1px solid rgba(43,29,201,.12);font-size:12px;color:${PRIMARY};font-weight:600}
  .live-dot{width:7px;height:7px;border-radius:50%;background:${PRIMARY};animation:pulse 2s ease-in-out infinite;display:inline-block}
  .divider{height:1px;background:${BORDER}}
  .deco-letter{font-size:clamp(200px,25vw,360px);font-weight:900;line-height:1;color:${BORDER};position:absolute;top:50%;transform:translateY(-50%);right:40px;pointer-events:none;user-select:none;opacity:.6;letter-spacing:-.05em}

  @media(max-width:900px){
    .hide-mob{display:none!important}
    .mob-grid-1{grid-template-columns:1fr!important}
    .deco-letter{display:none}
  }
`;

function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("visible"); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el); return () => obs.disconnect();
  }, []);
  return ref;
}

function useCounter(target, duration = 1800) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        obs.disconnect();
        let start = 0; const step = target / (duration / 16);
        const t = setInterval(() => {
          start = Math.min(start + step, target);
          setCount(Math.floor(start));
          if (start >= target) clearInterval(t);
        }, 16);
      }
    }, { threshold: 0.5 });
    obs.observe(el); return () => obs.disconnect();
  }, [target, duration]);
  return [count, ref];
}

function DashboardMockup() {
  const items = [
    { label: "Goals on Track", val: "8/10", color: PRIMARY,  icon: "🎯" },
    { label: "Habits Today",   val: "5/7",  color: AMBER,    icon: "⚡" },
    { label: "Monthly Saved",  val: "₹12k", color: "#2563eb", icon: "💰" },
    { label: "Streak",         val: "14d",  color: RED,      icon: "🔥" },
  ];
  return (
    <div className="app-win" style={{ width: "100%", maxWidth: 520 }}>
      <div className="app-bar">
        <div className="app-dot" style={{ background: "#ff5f57" }}/>
        <div className="app-dot" style={{ background: "#febc2e" }}/>
        <div className="app-dot" style={{ background: "#28c840" }}/>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginLeft: 8, fontFamily: FONT }}>Aspreo — Dashboard</span>
      </div>
      <div style={{ padding: 20, background: "#0e1a14" }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)", marginBottom: 4 }}>Good morning ✦</div>
          <div style={{ fontSize: 18, color: "#fff", fontWeight: 700, letterSpacing: "-.01em" }}>Design your day with intent.</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          {items.map(item => (
            <div key={item.label} style={{ background: "rgba(255,255,255,.05)", borderRadius: 10, padding: "11px 13px", border: "1px solid rgba(255,255,255,.07)" }}>
              <div style={{ fontSize: 16, marginBottom: 5 }}>{item.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: item.color, letterSpacing: "-.02em" }}>{item.val}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", marginTop: 2, textTransform: "uppercase", letterSpacing: ".05em" }}>{item.label}</div>
            </div>
          ))}
        </div>
        <div style={{ background: "rgba(255,255,255,.04)", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(255,255,255,.07)" }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.4)", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".07em" }}>Monthly Progress</div>
          {[["Vision Goals","78%",PRIMARY],["Health Habits","62%",AMBER],["Finance Target","91%","#2563eb"]].map(([l,p,c]) => (
            <div key={l} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>{l}</span>
                <span style={{ fontSize: 11, color: c, fontWeight: 700 }}>{p}</span>
              </div>
              <div style={{ height: 4, background: "rgba(255,255,255,.08)", borderRadius: 99 }}>
                <div style={{ width: p, height: "100%", background: c, borderRadius: 99 }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FinanceMockup() {
  return (
    <div className="app-win" style={{ width: "100%", maxWidth: 520 }}>
      <div className="app-bar">
        <div className="app-dot" style={{ background: "#ff5f57" }}/><div className="app-dot" style={{ background: "#febc2e" }}/><div className="app-dot" style={{ background: "#28c840" }}/>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginLeft: 8 }}>Aspreo — Finance Tracker</span>
      </div>
      <div style={{ padding: 20, background: "#0e1a14" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
          {[["Income","₹85,000",GREEN],["Expenses","₹42,300",RED],["Savings","₹42,700",PRIMARY]].map(([l,v,c]) => (
            <div key={l} style={{ background: "rgba(255,255,255,.05)", borderRadius: 10, padding: 11, border: `1px solid ${c}30` }}>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,.35)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".06em" }}>{l}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: c, letterSpacing: "-.02em" }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ background: "rgba(255,255,255,.04)", borderRadius: 10, padding: 13, border: "1px solid rgba(255,255,255,.07)" }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", marginBottom: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>Recent Transactions</div>
          {[["🏠 Home Loan EMI","Housing","₹18,000"],["🚗 Car EMI","Transport","₹10,000"],["🛒 Groceries","Food","₹3,200"],["💡 Electricity","Utilities","₹1,800"]].map(([d,c,a]) => (
            <div key={d} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
              <div><div style={{ fontSize: 12, color: "rgba(255,255,255,.75)" }}>{d}</div><div style={{ fontSize: 10, color: "rgba(255,255,255,.3)", marginTop: 1 }}>{c}</div></div>
              <div style={{ fontSize: 12, color: "#f87171", fontWeight: 700 }}>{a}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VaultMockup() {
  return (
    <div className="app-win" style={{ width: "100%", maxWidth: 520 }}>
      <div className="app-bar">
        <div className="app-dot" style={{ background: "#ff5f57" }}/><div className="app-dot" style={{ background: "#febc2e" }}/><div className="app-dot" style={{ background: "#28c840" }}/>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginLeft: 8 }}>Aspreo — Link Vault 🔐</span>
      </div>
      <div style={{ padding: 20, background: "#0e1a14" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, background: `${GREEN}18`, borderRadius: 10, padding: "10px 14px", border: `1px solid ${GREEN}30` }}>
          <span style={{ fontSize: 16 }}>🔒</span>
          <div><div style={{ fontSize: 12, color: "#4ade80", fontWeight: 700 }}>End-to-End Encrypted</div><div style={{ fontSize: 10, color: "rgba(255,255,255,.35)" }}>All credentials encrypted with your unique key</div></div>
        </div>
        {[{icon:"🐙",name:"GitHub",url:"github.com",auth:"User/Pass"},{icon:"📧",name:"Gmail Workspace",url:"mail.google.com",auth:"SSO"},{icon:"🏦",name:"HDFC NetBanking",url:"hdfcbank.com",auth:"OTP"},{icon:"☁️",name:"AWS Console",url:"aws.amazon.com",auth:"User/Pass"}].map(item => (
          <div key={item.name} style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
            <div style={{ width: 33, height: 33, borderRadius: 8, background: "rgba(255,255,255,.07)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>{item.icon}</div>
            <div style={{ flex: 1 }}><div style={{ fontSize: 13, color: "rgba(255,255,255,.8)", fontWeight: 500 }}>{item.name}</div><div style={{ fontSize: 10, color: "rgba(255,255,255,.3)" }}>{item.url}</div></div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: `${GREEN}20`, color: "#4ade80" }}>{item.auth}</span>
              <span style={{ fontSize: 12 }}>🔐</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HabitsMockup() {
  const days = ["M","T","W","T","F","S","S"];
  const habits = [
    { name: "Morning Meditation", done: [1,1,1,1,1,0,1], color: GREEN },
    { name: "Exercise 30min",     done: [1,1,0,1,1,1,0], color: AMBER },
    { name: "Read 20 pages",      done: [1,0,1,1,0,1,1], color: PRIMARY },
    { name: "No Sugar",           done: [1,1,1,0,1,1,1], color: RED },
  ];
  return (
    <div className="app-win" style={{ width: "100%", maxWidth: 520 }}>
      <div className="app-bar">
        <div className="app-dot" style={{ background: "#ff5f57" }}/><div className="app-dot" style={{ background: "#febc2e" }}/><div className="app-dot" style={{ background: "#28c840" }}/>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginLeft: 8 }}>Aspreo — Habit Tracker</span>
      </div>
      <div style={{ padding: 20, background: "#0e1a14" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 15, color: "#fff", fontWeight: 700 }}>This Week</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.35)" }}>Mar 10–16, 2026</div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 7, marginBottom: 10 }}>
          {days.map((d, i) => <div key={i} style={{ width: 26, textAlign: "center", fontSize: 10, color: "rgba(255,255,255,.3)", fontWeight: 600 }}>{d}</div>)}
        </div>
        {habits.map(h => (
          <div key={h.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
            <div style={{ flex: 1, fontSize: 11, color: "rgba(255,255,255,.65)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.name}</div>
            <div style={{ display: "flex", gap: 7 }}>
              {h.done.map((d, i) => (
                <div key={i} style={{ width: 26, height: 26, borderRadius: "50%", background: d ? h.color : "rgba(255,255,255,.07)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff" }}>
                  {d ? "✓" : ""}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div style={{ marginTop: 12, background: `${PRIMARY}18`, borderRadius: 10, padding: "9px 13px", border: `1px solid ${PRIMARY}30` }}>
          <div style={{ fontSize: 11, color: "#818cf8", fontWeight: 700 }}>🔥 14-day streak — Keep it up!</div>
        </div>
      </div>
    </div>
  );
}

const Icon = ({ name, size = 20, color, filled = false, style = {} }) => (
  <span className="material-symbols-outlined" style={{
    fontSize: size, color: color || "inherit",
    fontVariationSettings: filled ? "'FILL' 1,'wght' 500,'GRAD' 0,'opsz' 24" : undefined,
    ...style
  }}>{name}</span>
);

function Nav({ page, setPage, onLogin, onWaitlist }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
      height: 64, padding: "0 32px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: scrolled ? "rgba(246,246,248,.93)" : "transparent",
      backdropFilter: scrolled ? "blur(16px)" : "none",
      WebkitBackdropFilter: scrolled ? "blur(16px)" : "none",
      borderBottom: scrolled ? `1px solid ${BORDER}` : "1px solid transparent",
      transition: "all .28s ease",
    }}>
      <button onClick={() => setPage("home")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 9 }}>
        <div style={{ width: 32, height: 32, background: PRIMARY, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 3px 10px ${P_GLOW}` }}>
          <span style={{ fontSize:13, fontWeight:900, color:'#fff', lineHeight:1, fontFamily:'sans-serif' }}>A</span>
        </div>
        <span style={{ fontSize: 16, fontWeight: 800, color: TEXT, letterSpacing: "-.02em", fontFamily: FONT }}>Aspreo</span>
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 2 }} className="hide-mob">
        {[["home","Home"],["about","About"],["vision","Vision"]].map(([id,label]) => (
          <button key={id} className={`s-nav-link ${page === id ? "active" : ""}`} onClick={() => setPage(id)}>{label}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className="s-btn s-btn-ghost" style={{ fontSize: 13, padding: "7px 16px" }} onClick={onLogin}>Sign In</button>
        <button className="s-btn s-btn-primary" style={{ fontSize: 13, padding: "8px 20px" }} onClick={onLogin}>Get Started</button>
      </div>
    </nav>
  );
}

function EcosystemCarousel() {
  const cards = [
    { icon: "dashboard",            badge: "Kanban Style",     title: "Unified Dashboard",    desc: "Finance stats, kanban, habits, vision board — your command centre.", visual: "dashboard" },
    { icon: "target",               badge: "Growth Engine",    title: "Strategic Goals",      desc: "Break 10-year visions into daily actions with dot-grid progress.", visual: "goals" },
    { icon: "payments",             badge: "Financial Health", title: "Finance Tracker",      desc: "Income, expenses, subscriptions, and EMIs. Monthly balance in seconds.", visual: "finance" },
    { icon: "auto_awesome",         badge: "Visualisation",    title: "Vision Board",         desc: "High-fidelity vision cards. Keep your 'why' front and centre.", visual: "vision" },
    { icon: "local_fire_department",badge: "Streak Machine",   title: "Habit Tracker",        desc: "Daily check-ins, 7-day trends, streak counters, and milestone sharing.", visual: "habits" },
    { icon: "folder_open",          badge: "Collaboration",    title: "Projects & Teams",     desc: "Invite members, assign tasks, track project progress together.", visual: "projects" },
    { icon: "book",                 badge: "Visual Library",   title: "Reading Tracker",      desc: "Track progress, save quotes, upload covers, build your bookshelf.", visual: "books" },
    { icon: "groups",               badge: "Social Momentum",  title: "Accountability Feed",  desc: "Share milestones, build momentum through social motivation.", visual: "feed" },
    { icon: "lightbulb",            badge: "Creative Space",   title: "Ideas Board",          desc: "Capture raw ideas before they slip. Develop into full projects.", visual: "ideas" },
    { icon: "event_repeat",         badge: "Wealth Guard",     title: "Subscription Manager", desc: "Never miss a renewal. Smart alerts for every recurring payment.", visual: "subs" },
  ];
  const CardVisual = ({ type }) => {
    if (type === "dashboard") return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>{[[PRIMARY,"65%"],[AMBER,"40%"],[GREEN,"85%"]].map(([c,w],i)=><div key={i} style={{ background: `${c}12`, borderRadius: 7, padding: 8, border: `1px solid ${c}18` }}><div style={{ height: 5, background: `${c}30`, borderRadius: 3, marginBottom: 3 }}/><div style={{ height: 4, width: w, background: c, borderRadius: 99 }}/></div>)}</div>;
    if (type === "goals") return <div>{[["Portfolio","65%"],["React","85%"]].map(([l,p])=><div key={l} style={{ marginBottom: 7 }}><div style={{ display:"flex", justifyContent:"space-between", fontSize: 9, fontWeight: 700, color: TEXT2, marginBottom: 3 }}><span>{l}</span><span style={{color:PRIMARY}}>{p}</span></div><div style={{ background: BORDER, borderRadius: 99, height: 4, overflow: "hidden" }}><div style={{ width: p, height: "100%", background: PRIMARY, borderRadius: 99 }}/></div></div>)}</div>;
    if (type === "finance") return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5, textAlign: "center" }}>{[["₹1L",GREEN,"Income"],["₹54K",RED,"Expenses"],["₹46K",PRIMARY,"Balance"]].map(([v,c,l])=><div key={l}><div style={{ fontSize: 13, fontWeight: 800, color: c }}>{v}</div><div style={{ fontSize: 8, color: TEXT3, textTransform: "uppercase", fontWeight: 700 }}>{l}</div></div>)}</div>;
    if (type === "vision") return <div style={{ background: `linear-gradient(135deg,${PRIMARY},#7c3aed)`, borderRadius: 8, padding: 12, color: "#fff", textAlign: "center" }}><div style={{ fontSize: 8, fontWeight: 700, opacity: .7, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 3 }}>Vision Board 2026</div><div style={{ fontSize: 11, fontWeight: 800, lineHeight: 1.4 }}>"Build things that matter."</div></div>;
    if (type === "habits") return <div>{[["📖 Read",5],["🧘 Meditate",7]].map(([n,f])=><div key={n} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}><span style={{ fontSize: 10, fontWeight: 600, color: TEXT }}>{n}</span><div style={{ display: "flex", gap: 3 }}>{[...Array(7)].map((_,i)=><div key={i} style={{ width: 12, height: 12, borderRadius: 3, background: i < f ? PRIMARY : BORDER }}/>)}</div></div>)}</div>;
    if (type === "projects") return <div><div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:7 }}>{[[PRIMARY,"GK"],["#7c3aed","AJ"],[AMBER,"SM"]].map(([c,i])=><div key={i} style={{ width:20,height:20,borderRadius:"50%",background:c,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff",marginLeft:-4 }}>{i}</div>)}<span style={{ fontSize:10,color:TEXT3,marginLeft:4 }}>3 members</span></div><div style={{ background:BG,borderRadius:6,padding:"5px 8px",fontSize:10,fontWeight:500,color:TEXT2,border:`1px solid ${BORDER}` }}>Design system → <span style={{color:PRIMARY,fontWeight:700}}>AJ</span></div></div>;
    if (type === "books") return <div style={{ display:"flex",gap:7,alignItems:"flex-end" }}>{[[PRIMARY,52],[GREEN,46],[AMBER,54]].map(([c,h],i)=><div key={i} style={{ width:34,height:h,background:`linear-gradient(135deg,${c}88,${c})`,borderRadius:5,borderLeft:`3px solid ${c}` }}/>)}<div style={{ width:34,height:40,background:BG,borderRadius:5,border:`2px dashed ${BORDER}`,display:"flex",alignItems:"center",justifyContent:"center" }}><Icon name="add" size={14} color={TEXT3}/></div></div>;
    if (type === "feed") return <div style={{ display:"flex",flexDirection:"column",gap:6 }}>{[["RK","🔥 21-day streak!",GREEN],["SN","✅ Goal 85% done",PRIMARY]].map(([init,msg,c])=><div key={init} style={{ display:"flex",alignItems:"center",gap:7,padding:"5px 8px",background:BG,borderRadius:7,border:`1px solid ${BORDER}` }}><div style={{ width:22,height:22,borderRadius:"50%",background:c,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff",flexShrink:0 }}>{init}</div><div style={{ fontSize:10,fontWeight:600,color:TEXT }}>{msg}</div></div>)}</div>;
    if (type === "ideas") return <div style={{ position:"relative",height:60,overflow:"hidden" }}>{[[8,36,40,.15,0],[60,24,28,.1,1],[110,44,48,.2,2],[180,28,32,.12,.5]].map(([l,w,h,o,delay],i)=><div key={i} style={{ position:"absolute",bottom:4,left:l,width:w,height:h,borderRadius:"50%",background:`rgba(43,29,201,${o})`,animation:`bubble ${3+i*.5}s ease-in-out infinite ${delay}s` }}/>)}</div>;
    if (type === "subs") return <div style={{ display:"flex",flexDirection:"column",gap:5 }}><div style={{ display:"flex",justifyContent:"space-between",padding:"5px 8px",background:`rgba(220,38,38,.06)`,borderRadius:6,border:`1px solid rgba(220,38,38,.15)` }}><span style={{ fontSize:10,fontWeight:600 }}>Netflix</span><span style={{ fontSize:10,fontWeight:700,color:RED }}>↺ 2 days</span></div><div style={{ display:"flex",justifyContent:"space-between",padding:"5px 8px",background:BG,borderRadius:6,border:`1px solid ${BORDER}` }}><span style={{ fontSize:10,fontWeight:600 }}>Spotify</span><span style={{ fontSize:10,color:TEXT3 }}>₹179/mo</span></div></div>;
    return null;
  };
  const renderCards = (key) => cards.map((c, i) => (
    <div key={`${key}-${i}`} className="c-card" aria-hidden={key === "dup"}>
      <div className="c-icon"><Icon name={c.icon} size={20} color={PRIMARY}/></div>
      <div style={{ fontSize: 9, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 7 }}>{c.badge}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: TEXT, letterSpacing: "-.01em", marginBottom: 8 }}>{c.title}</div>
      <div style={{ fontSize: 12, color: TEXT2, lineHeight: 1.6, marginBottom: 14 }}>{c.desc}</div>
      <div style={{ background: BG, borderRadius: 9, padding: 11, border: `1px solid ${BORDER}` }}><CardVisual type={c.visual}/></div>
    </div>
  ));
  return (
    <div className="carousel-wrap">
      <div className="carousel-track">{renderCards("a")}{renderCards("dup")}</div>
    </div>
  );
}

function HomePage({ onLogin, onWaitlist }) {
  const [c1, ref1] = useCounter(12000);
  const [c2, ref2] = useCounter(98);
  const [c3, ref3] = useCounter(200);
  const r1=useScrollReveal(), r2=useScrollReveal(), r3=useScrollReveal(),
        r4=useScrollReveal(), r5=useScrollReveal(), r6=useScrollReveal(),
        r7=useScrollReveal(), r8=useScrollReveal();

  return (
    <div>
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section style={{ minHeight:"100vh", display:"flex", alignItems:"center", padding:"120px 48px 80px", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 65% 35%, ${P_LIGHT} 0%, transparent 55%), radial-gradient(circle at 15% 75%, rgba(37,99,235,.06) 0%, transparent 45%)`, pointerEvents:"none" }}/>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle, ${BORDER} 1px, transparent 1px)`, backgroundSize:"20px 20px", opacity:.5, pointerEvents:"none" }}/>
        <div className="deco-letter">A</div>
        <div style={{ maxWidth:860, position:"relative", zIndex:1 }}>
          <div className="fade-in" style={{ display:"flex", gap:10, marginBottom:28, flexWrap:"wrap" }}>
            <span className="trust-badge"><span className="live-dot"/>Live data sync</span>
            <span className="trust-badge">🔐 AES-256 Encrypted</span>
            <span className="trust-badge">🇮🇳 Built for India</span>
            <span className="trust-badge">🔒 Invite-only · Join waitlist</span>
          </div>
          <h1 className="fade-up delay-1" style={{ fontSize:"clamp(44px,7vw,86px)", fontWeight:900, lineHeight:1.06, letterSpacing:"-.035em", color:TEXT, marginBottom:24 }}>
            Your whole life,<br/><span className="grad-text">one platform.</span>
          </h1>
          <p className="fade-up delay-2" style={{ fontSize:19, color:TEXT2, lineHeight:1.75, maxWidth:620, marginBottom:36, fontWeight:300 }}>
            Aspreo combines your goals, habits, and finances, a full personal knowledge library, Indian-diet-aware fitness tracking, and a lifelong reflection journal — plus client invoicing and project management — into one beautifully integrated workspace.
          </p>
          <div className="fade-up delay-3" style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:48 }}>
            <button className="s-btn s-btn-primary" style={{ fontSize:16, padding:"14px 32px" }} onClick={onLogin}>
              Get Started — Free, No Card Needed <Icon name="arrow_forward" size={18} color="#fff"/>
            </button>
            <button className="s-btn s-btn-ghost" style={{ fontSize:15, padding:"14px 24px" }} onClick={onWaitlist}>Join the Waitlist →</button>
          </div>
          <div className="fade-up delay-5" style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
            {[
              ["🧠","Second Brain","Notes, books, articles & more"],
              ["🥗","Fitness & Diet","Indian nutrition + workouts"],
              ["📔","Reflection & Journal","Daily entries, mood trends"],
              ["📱","Works Everywhere","Web, mobile, PWA"],
            ].map(([icon,label,sub]) => (
              <div key={label} style={{ padding:"11px 16px", background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:11, boxShadow:SHADOW_SM, display:"flex", gap:10, alignItems:"center" }}>
                <span style={{ fontSize:18 }}>{icon}</span>
                <div><div style={{ fontSize:12, fontWeight:700, color:TEXT }}>{label}</div><div style={{ fontSize:11, color:TEXT3 }}>{sub}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── STATS ──────────────────────────────────────────────────────────── */}
      <section style={{ padding:"60px 48px", background:SURFACE }}>
        <div ref={r1} className="scroll-reveal" style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:40, textAlign:"center" }}>
          <div><div ref={ref1} className="stat-num" style={{ color:PRIMARY }}>{c1.toLocaleString()}+</div><div style={{ fontSize:13, color:TEXT2, marginTop:6 }}>Active users</div></div>
          <div><div ref={ref2} className="stat-num" style={{ color:TEXT }}>{c2}%</div><div style={{ fontSize:13, color:TEXT2, marginTop:6 }}>Data encrypted at rest</div></div>
          <div><div ref={ref3} className="stat-num" style={{ color:PRIMARY }}>{c3}+</div><div style={{ fontSize:13, color:TEXT2, marginTop:6 }}>Indian foods & exercises in the library</div></div>
          <div><div className="stat-num" style={{ color:PRIMARY }}>8</div><div style={{ fontSize:13, color:TEXT2, marginTop:6 }}>Integrated modules</div></div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── MODULES OVERVIEW ───────────────────────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:BG }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r2} className="scroll-reveal" style={{ textAlign:"center", marginBottom:56 }}>
            <div className="sec-tag" style={{ marginBottom:14 }}>The Full Platform</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,52px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.1, marginBottom:16 }}>
              Everything your life needs.<br/><span style={{ color:PRIMARY }}>Nothing you don't.</span>
            </h2>
            <p style={{ fontSize:16, color:TEXT2, maxWidth:520, margin:"0 auto", lineHeight:1.7 }}>
              From your morning journal to your workout log to your next big idea — Aspreo replaces a dozen different apps with one connected workspace. Every module, every account, no locked features.
            </p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
            {[
              { icon:"🎯", color:"#7C3AED", title:"Personal Productivity", sub:"Included", items:["Goals with sub-tasks & dot tracker","Vision board with life pillars","Daily habits & streak tracking","Weekly & monthly planners","Finance tracker with EMI management","Book tracker & reading log"] },
              { icon:"🧠", color:"#2B1DC9", title:"Knowledge — Second Brain", sub:"Included", items:["11 content types: notes, books, articles, videos & more","Domains, collections & tags to stay organized","Markdown editor with live preview","File, image & PDF attachments","Related-knowledge linking","Full version history"] },
              { icon:"🥗", color:"#0D9488", title:"Fitness & Diet", sub:"Included", items:["130+ Indian foods with full macros","Reusable weekly meal plans","Water & supplement tracking","75+ exercise library & workout plans","Body composition & BMI tracking","Health goals with progress tracking"] },
              { icon:"📔", color:"#D97706", title:"Reflection & Journal", sub:"Included", items:["6 daily entry types with their own prompts","Weekly, Monthly & Yearly Reviews","Life Events & Milestones","Calendar and timeline views","Mood trends & writing streaks","Full version history"] },
              { icon:"⏱️", color:"#8A5A6B", title:"Deep Work & Projects", sub:"Included", items:["Focus timer sessions with categories","Kanban project boards","Task assignment & activity logs","Time tracking per task","Recurring tasks & shared templates","Unified calendar across everything"] },
              { icon:"💼", color:"#059669", title:"Client & Invoicing", sub:"Included", items:["Client records with full history","Professional invoices with shareable links","Consultant roster with resume/photo","Business profile & bank details","Encrypted link vault for credentials","GST-ready, India-first"] },
            ].map(m => (
              <div key={m.title} style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:16, padding:24, boxShadow:SHADOW_SM, transition:"transform .2s, box-shadow .2s" }}
                onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow=SHADOW_LG; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=SHADOW_SM; }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                  <div style={{ width:48, height:48, borderRadius:12, background:m.color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>{m.icon}</div>
                  <span style={{ fontSize:10, padding:"3px 9px", borderRadius:99, background:m.color+"18", color:m.color, fontWeight:700 }}>{m.sub}</span>
                </div>
                <div style={{ fontWeight:800, fontSize:16, color:TEXT, marginBottom:4 }}>{m.title}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:5, marginTop:12 }}>
                  {m.items.map((item,i) => (
                    <div key={i} style={{ display:"flex", gap:8, fontSize:13, color:TEXT2, alignItems:"flex-start" }}>
                      <span style={{ color:m.color, fontWeight:700, flexShrink:0, marginTop:1 }}>✓</span>{item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── HOW IT WORKS ───────────────────────────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:SURFACE }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r3} className="scroll-reveal" style={{ textAlign:"center", marginBottom:56 }}>
            <div className="sec-tag" style={{ marginBottom:14 }}>How It Works</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.15, marginBottom:14 }}>
              From signup to daily habit — in minutes
            </h2>
            <p style={{ fontSize:16, color:TEXT2, maxWidth:480, margin:"0 auto", lineHeight:1.7 }}>
              No complex onboarding. No hidden tiers. Every module is available to every account from the moment you sign in.
            </p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:0, position:"relative" }}>
            <div style={{ position:"absolute", top:44, left:"12.5%", right:"12.5%", height:2, background:`linear-gradient(90deg, ${PRIMARY}, #7C3AED, #0D9488, #D97706)`, zIndex:0, opacity:.3 }}/>
            {[
              { step:"01", icon:"🌟", color:PRIMARY,    title:"Set Your Vision",     body:"Define your life goals, habits, and monthly priorities. Your personal system is ready instantly." },
              { step:"02", icon:"🧠", color:"#7C3AED", title:"Build Your Second Brain", body:"Capture notes, books, articles, and ideas as you go. Organize them into domains and collections." },
              { step:"03", icon:"🥗", color:"#0D9488", title:"Track Body & Mind",      body:"Log meals from the Indian food library, track workouts, and write your daily reflection." },
              { step:"04", icon:"📈", color:"#D97706", title:"Stay Consistent",body:"Dashboards, streaks, and weekly/monthly reviews keep you coming back and moving forward." },
            ].map(s => (
              <div key={s.step} style={{ textAlign:"center", padding:"0 24px", position:"relative", zIndex:1 }}>
                <div style={{ width:88, height:88, borderRadius:"50%", background:s.color+"15", border:`2px solid ${s.color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:36, margin:"0 auto 20px" }}>{s.icon}</div>
                <div style={{ fontSize:11, fontWeight:700, color:s.color, textTransform:"uppercase", letterSpacing:".1em", marginBottom:6 }}>Step {s.step}</div>
                <div style={{ fontWeight:800, fontSize:16, color:TEXT, marginBottom:8 }}>{s.title}</div>
                <div style={{ fontSize:13, color:TEXT2, lineHeight:1.65 }}>{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── KNOWLEDGE / SECOND BRAIN SHOWCASE ───────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:BG }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center" }}>
          <div ref={r4} className="scroll-reveal">
            <div className="sec-tag" style={{ marginBottom:14 }}>Second Brain</div>
            <h2 style={{ fontSize:"clamp(26px,3.5vw,44px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.15, marginBottom:16 }}>
              Your knowledge, organized and always searchable
            </h2>
            <p style={{ fontSize:15, color:TEXT2, lineHeight:1.75, marginBottom:24 }}>
              Capture notes, books, articles, videos, quotes, and ideas in one typed library. Organize with <strong style={{ color:PRIMARY }}>domains and collections</strong>, tag freely, link related knowledge together — and never lose a thought again.
            </p>
            {[
              "11 content types: notes, books, articles, videos & more",
              "Domains & collections keep everything organized",
              "Markdown editor with live preview and attachments",
              "Manual related-knowledge linking between items",
              "Full version history on every item",
            ].map((f,i) => (
              <div key={i} style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8, fontSize:14, color:TEXT2 }}>
                <span style={{ color:GREEN, fontWeight:700 }}>✓</span>{f}
              </div>
            ))}
            <button className="s-btn s-btn-primary" style={{ marginTop:24 }} onClick={onLogin}>Build Your Second Brain</button>
          </div>
          <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:20, padding:28, boxShadow:SHADOW_MD }}>
            <div style={{ fontSize:12, color:TEXT3, marginBottom:16, textAlign:"center" }}>Recently added</div>
            {[
              { icon:"📝", label:"Note — Q3 product ideas", meta:"Ideas · 2 tags" },
              { icon:"📚", label:"Book — Atomic Habits", meta:"Personal Growth · 68% read" },
              { icon:"📰", label:"Article — Compound interest, explained", meta:"Finance · 3 tags" },
              { icon:"▶️", label:"YouTube — Deep work masterclass", meta:"Focus · related to 2 items" },
              { icon:"💡", label:"Idea — Weekend habit reset", meta:"Personal Growth" },
            ].map((row,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:i<4?`1px solid ${BORDER}`:"none" }}>
                <div style={{ display:"flex", gap:10, alignItems:"center", fontSize:13, color:TEXT }}>
                  <span>{row.icon}</span>{row.label}
                </div>
                <span style={{ fontSize:11, color:TEXT3 }}>{row.meta}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── FITNESS & DIET SHOWCASE ────────────────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:SURFACE }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center" }}>
          <div style={{ background:BG, border:`1px solid ${BORDER}`, borderRadius:20, padding:28, boxShadow:SHADOW_MD }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#0D9488", textTransform:"uppercase", letterSpacing:".08em", marginBottom:16 }}>Today's macros</div>
            {[
              { icon:"🔥", label:"Calories", value:"1,540 / 2,000", color:"#0D9488", bold:true },
              { icon:"🍗", label:"Protein",  value:"88g / 120g",    color:GREEN },
              { icon:"🍚", label:"Carbs",    value:"170g / 220g",   color:GREEN },
              { icon:"🥑", label:"Fat",      value:"48g / 65g",     color:GREEN },
              { icon:"🏋️", label:"Workout streak", value:"12 days", color:"#0D9488" },
            ].map((row,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:i<4?`1px solid ${BORDER}`:"none" }}>
                <div style={{ display:"flex", gap:10, alignItems:"center", fontSize:13, color:TEXT2 }}>
                  <span>{row.icon}</span>{row.label}
                </div>
                <span style={{ fontSize:13, fontWeight:row.bold?800:600, color:row.color }}>{row.value}</span>
              </div>
            ))}
          </div>
          <div ref={r5} className="scroll-reveal">
            <div className="sec-tag" style={{ marginBottom:14 }}>Fitness & Diet</div>
            <h2 style={{ fontSize:"clamp(26px,3.5vw,44px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.15, marginBottom:16 }}>
              A complete health system, not just a calorie counter
            </h2>
            <p style={{ fontSize:15, color:TEXT2, lineHeight:1.75, marginBottom:24 }}>
              Log meals from a <strong style={{ color:"#0D9488" }}>130+ item Indian food library</strong>, track workouts from a 75+ exercise library, monitor body composition, and set real health goals — Fat Loss, Muscle Gain, Strength, Endurance, and more.
            </p>
            {[
              "130+ Indian foods with full macro breakdowns",
              "Reusable weekly meal plans, water & supplement tracking",
              "Workout plans, exercise library & per-set logging",
              "Body composition check-ins and BMI tracking",
              "Health goals with real progress tracking",
            ].map((f,i) => (
              <div key={i} style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8, fontSize:14, color:TEXT2 }}>
                <span style={{ color:"#0D9488", fontWeight:700 }}>✓</span>{f}
              </div>
            ))}
            <button className="s-btn s-btn-primary" style={{ marginTop:24, background:"#0D9488", border:"none" }} onClick={onLogin}>Start Tracking →</button>
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── PERSONAL PRODUCTIVITY ──────────────────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:BG }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r6} className="scroll-reveal" style={{ textAlign:"center", marginBottom:56 }}>
            <div className="sec-tag" style={{ marginBottom:14 }}>Personal Growth</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.1, marginBottom:14 }}>
              Your life system, not just your work system
            </h2>
            <p style={{ fontSize:16, color:TEXT2, maxWidth:520, margin:"0 auto", lineHeight:1.7 }}>
              Aspreo brings your goals, health, knowledge, and reflections together in one place — because personal growth and getting things done were never really separate.
            </p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
            {[
              { icon:"🌟", color:PRIMARY,  title:"Vision Architecture",  body:"Create life pillars across health, career, relationships, finance. Build a vision board with image uploads. See your 'why' every day." },
              { icon:"🎯", color:"#7C3AED",title:"Goal System",          body:"Break big ambitions into structured milestones. Dot-grid progress tracking, sub-tasks, deadlines, and reminder alerts." },
              { icon:"🔥", color:AMBER,    title:"Habit Tracker",        body:"Visual weekly habit grid, streak counters, and daily completion rings. Build identity through daily consistency." },
              { icon:"📅", color:GREEN,    title:"Weekly & Monthly Plan", body:"Plan your week with a focus goal, daily task lists, and recurring events. Monthly anchor and top-3 priorities." },
              { icon:"💰", color:RED,      title:"Finance Tracker",      body:"Income, expenses, EMIs, and subscriptions. Monthly balance overview with category breakdowns and savings goals." },
              { icon:"📚", color:"#0D9488",title:"Book Tracker",         body:"Track reading progress, key learnings, and book summaries. Stay accountable to your reading habit." },
            ].map(f => (
              <div key={f.title} style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:14, padding:22, boxShadow:SHADOW_SM }}>
                <div style={{ width:44, height:44, borderRadius:11, background:f.color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, marginBottom:14 }}>{f.icon}</div>
                <div style={{ fontWeight:700, fontSize:15, color:TEXT, marginBottom:8 }}>{f.title}</div>
                <div style={{ fontSize:13, color:TEXT2, lineHeight:1.65 }}>{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── REFLECTION & JOURNAL ───────────────────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:SURFACE }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center" }}>
          <div ref={r7} className="scroll-reveal">
            <div className="sec-tag" style={{ marginBottom:14 }}>Reflection & Journal</div>
            <h2 style={{ fontSize:"clamp(26px,3.5vw,44px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.15, marginBottom:16 }}>
              A lifelong journal that's actually worth opening daily
            </h2>
            <p style={{ fontSize:15, color:TEXT2, lineHeight:1.75, marginBottom:24 }}>
              Six daily entry types — Daily Journal, Morning Reflection, Evening Review, Gratitude, Wins, and Lessons — plus Weekly, Monthly, and Yearly Reviews. Calendar and timeline views, mood trends, and writing streaks keep you coming back.
            </p>
            {[
              "6 daily entry types, each with its own prompts",
              "Weekly, Monthly & Yearly Reviews plus Life Events",
              "Calendar and timeline views over your whole history",
              "14-day mood trend and writing streaks",
              "Markdown editor, tags, favorites & version history",
              "Recent gratitude, wins & reflections dashboard",
            ].map((f,i) => (
              <div key={i} style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8, fontSize:14, color:TEXT2 }}>
                <span style={{ color:"#8A5A6B", fontWeight:700 }}>✓</span>{f}
              </div>
            ))}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              { label:"Writing Streak", icon:"🔥", color:"#8A5A6B", value:"12", sub:"days" },
              { label:"This Week",      icon:"📔", color:"#0D9488", value:"8",  sub:"entries" },
              { label:"This Month",     icon:"📅", color:"#7C3AED", value:"24", sub:"entries" },
              { label:"Reviews Written",icon:"🗓️", color:"#D97706", value:"3",  sub:"this month" },
            ].map(s => (
              <div key={s.label} style={{ background:BG, border:`1px solid ${s.color}30`, borderRadius:12, padding:16, borderTop:`3px solid ${s.color}` }}>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12 }}>
                  <span style={{ fontSize:20 }}>{s.icon}</span>
                  <span style={{ fontWeight:700, fontSize:13, color:TEXT }}>{s.label}</span>
                </div>
                <div style={{ fontSize:22, fontWeight:900, color:s.color, marginBottom:2 }}>{s.value}</div>
                <div style={{ fontSize:11, color:TEXT3 }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── TESTIMONIALS ───────────────────────────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:BG }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r8} className="scroll-reveal" style={{ textAlign:"center", marginBottom:48 }}>
            <div className="sec-tag" style={{ marginBottom:14 }}>What Users Say</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,44px)", fontWeight:900, letterSpacing:"-.025em" }}>Built for people who build</h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
            {[
              { name:"Priya Sharma",    role:"Freelance Designer, Mumbai",     avatar:"PS", color:"#7C3AED", text:"I finally have one place for my goals, my reading list, and my daily reflections. The Second Brain replaced three different note apps I was juggling between." },
              { name:"Rahul Mehta",     role:"Fitness Coach, Delhi",           avatar:"RM", color:"#0D9488", text:"The food library actually understands Indian meals — I'm not manually calculating macros for dal and roti anymore. The workout streak keeps me honest too." },
              { name:"Anjali Patel",    role:"Writer, Bangalore",              avatar:"AP", color:"#8A5A6B", text:"My journal used to be a single mood note. Now I've got Morning Reflections, Weekly Reviews, and a mood trend that's shown me patterns I never noticed before." },
            ].map(t => (
              <div key={t.name} style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:16, padding:24, boxShadow:SHADOW_SM }}>
                <div style={{ fontSize:32, color:t.color, marginBottom:12, lineHeight:1 }}>"</div>
                <p style={{ fontSize:14, color:TEXT2, lineHeight:1.75, marginBottom:20 }}>{t.text}</p>
                <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                  <div style={{ width:40, height:40, borderRadius:"50%", background:t.color, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:14, flexShrink:0 }}>{t.avatar}</div>
                  <div><div style={{ fontWeight:700, fontSize:14, color:TEXT }}>{t.name}</div><div style={{ fontSize:12, color:TEXT3 }}>{t.role}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── WAITLIST TEASER ─────────────────────────────────────────────────── */}
      <section style={{ padding:"100px 48px", background:DARK, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 20% 50%, rgba(43,29,201,.4) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(124,58,237,.3) 0%, transparent 50%)`, pointerEvents:"none" }}/>
        <div style={{ maxWidth:640, margin:"0 auto", textAlign:"center", position:"relative", zIndex:1 }}>
          <div className="sec-tag" style={{ background:"rgba(255,255,255,.1)", color:"rgba(255,255,255,.8)", borderColor:"rgba(255,255,255,.15)", marginBottom:20 }}>Early Access</div>
          <h2 style={{ fontSize:"clamp(32px,5vw,56px)", fontWeight:900, letterSpacing:"-.03em", color:"#fff", marginBottom:16, lineHeight:1.08 }}>
            We're building something<br/><span style={{ background:"linear-gradient(90deg,#818CF8,#A78BFA)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>big.</span>
          </h2>
          <p style={{ fontSize:17, color:"rgba(255,255,255,.65)", maxWidth:480, margin:"0 auto 36px", lineHeight:1.7 }}>
            Aspreo is currently invite-only. Join the waitlist and be the first to know when we open up — and get exclusive early-access benefits.
          </p>
          <button className="s-btn s-btn-primary" style={{ fontSize:16, padding:"15px 40px" }} onClick={onWaitlist}>
            Join the Waitlist →
          </button>
          <div style={{ marginTop:16, fontSize:12, color:"rgba(255,255,255,.35)" }}>No spam. No pricing. Just updates when it matters.</div>
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────────────────────── */}
      <section style={{ padding:"120px 48px", background:SURFACE, textAlign:"center" }}>
        <div style={{ maxWidth:700, margin:"0 auto" }}>
          <div className="sec-tag" style={{ marginBottom:20 }}>Get Started</div>
          <h2 style={{ fontSize:"clamp(32px,5vw,60px)", fontWeight:900, letterSpacing:"-.03em", lineHeight:1.08, marginBottom:20 }}>
            Your business and life system — <span className="grad-text">starts today.</span>
          </h2>
          <p style={{ fontSize:17, color:TEXT2, maxWidth:480, margin:"0 auto 36px", lineHeight:1.7 }}>
            Join everyone building their life system on Aspreo — goals, knowledge, fitness, and reflection, all in one place. Free for every account, full access from day one.
          </p>
          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <button className="s-btn s-btn-primary" style={{ fontSize:16, padding:"15px 40px" }} onClick={onLogin}>
              Get Started Free <Icon name="arrow_forward" size={18} color="#fff"/>
            </button>
            <button className="s-btn s-btn-ghost" style={{ fontSize:15 }} onClick={onWaitlist}>Join the Waitlist →</button>
          </div>
          <div style={{ marginTop:28, fontSize:13, color:TEXT3 }}>
            Invite-only access · Join the waitlist to get notified
          </div>
        </div>
      </section>
    </div>
  );
}


function AboutPage() {
  const r1=useScrollReveal(), r2=useScrollReveal(), r3=useScrollReveal(), r4=useScrollReveal();
  return (
    <div>
      {/* Hero */}
      <section style={{ padding:"160px 48px 100px", background:BG, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 60% 40%, ${P_LIGHT} 0%, transparent 50%)`, pointerEvents:"none" }}/>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle, ${BORDER} 1px, transparent 1px)`, backgroundSize:"20px 20px", opacity:.5, pointerEvents:"none" }}/>
        <div style={{ maxWidth:760, position:"relative", zIndex:1 }} className="fade-up">
          <div className="sec-tag" style={{ marginBottom:22 }}>About Aspreo</div>
          <h1 style={{ fontSize:"clamp(40px,6vw,72px)", fontWeight:900, lineHeight:1.08, letterSpacing:"-.035em", marginBottom:24 }}>
            One platform for your<br/><em style={{ fontStyle:"italic", color:PRIMARY }}>whole life.</em>
          </h1>
          <p style={{ fontSize:18, color:TEXT2, lineHeight:1.8, fontWeight:300, maxWidth:620 }}>
            We built Aspreo because nobody should have to juggle 12 different apps to run their life. Your goals, your knowledge, your health, your reflections, your clients — all in one place.
          </p>
        </div>
      </section>

      <div className="divider"/>

      {/* The problem we solve */}
      <section style={{ padding:"96px 48px", maxWidth:1100, margin:"0 auto" }}>
        <div ref={r1} className="scroll-reveal" style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:72, alignItems:"start" }}>
          <div>
            <div className="sec-tag" style={{ marginBottom:14 }}>The Problem</div>
            <div style={{ width:1, height:120, background:`linear-gradient(${PRIMARY},transparent)`, marginTop:20, marginLeft:20 }}/>
          </div>
          <div>
            <h2 style={{ fontSize:"clamp(24px,3.5vw,40px)", fontWeight:900, letterSpacing:"-.025em", marginBottom:20, lineHeight:1.15 }}>
              The average person uses 8 apps to run their life. We replaced all of them.
            </h2>
            <p style={{ fontSize:17, color:TEXT, lineHeight:1.85, marginBottom:18, fontWeight:300 }}>
              Notion for notes. Day One for journaling. MyFitnessPal for macros. Zoho for invoices. A habit app. A goal-setting app. A separate app for books. The list never ends — and so do the monthly subscriptions.
            </p>
            <p style={{ fontSize:15, color:TEXT2, lineHeight:1.8, marginBottom:18 }}>
              Aspreo unifies your personal knowledge library, Indian-diet-aware fitness tracking, lifelong reflection journal, goals & habits, and client invoicing into a single, beautifully designed workspace. One login. One dashboard. Full access for everyone.
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginTop:28 }}>
              {[
                { before:"MyFitnessPal (₹800/mo)", after:"Aspreo Fitness & Diet", color:"#DC2626" },
                { before:"Zoho Invoice (₹1,200/mo)", after:"Aspreo Invoices", color:"#DC2626" },
                { before:"Day One (₹600/mo)", after:"Aspreo Reflection & Journal", color:"#DC2626" },
                { before:"Notion (₹800/mo)", after:"Aspreo Knowledge & Planner", color:"#DC2626" },
              ].map(r => (
                <div key={r.before} style={{ background:BG, border:`1px solid ${BORDER}`, borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:11, color:r.color, fontWeight:700, marginBottom:3, textDecoration:"line-through" }}>{r.before}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:GREEN }}>✓ {r.after}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* What we believe */}
      <section style={{ padding:"96px 48px", background:SURFACE }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r2} className="scroll-reveal" style={{ textAlign:"center", marginBottom:56 }}>
            <div className="sec-tag" style={{ marginBottom:18 }}>Our Philosophy</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.1 }}>
              Built on three core beliefs
            </h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
            {[
              { n:"01", title:"Personal and professional life are one.", body:"The most successful people don't separate their work goals from their personal ones. Aspreo is the only platform that manages both — because your morning habit streak and your client pipeline are equally important.", color:PRIMARY },
              { n:"02", title:"Automation should free you, not confuse you.", body:"When someone books a meeting, their calendar should update. When someone pays, their course access should arrive. When a lead replies, you should know. Aspreo handles the entire chain so you can focus on what only humans can do.", color:"#7C3AED" },
              { n:"03", title:"Every user gets everything. No tiers, no locked features.", body:"Whether you're tracking your first habit or managing invoices for a growing client list, you get the full platform from day one — knowledge library, fitness tracking, reflection journal, and client tools included.", color:"#0D9488" },
            ].map(b => (
              <div key={b.n} style={{ padding:"32px 28px", background:BG, border:`1px solid ${BORDER}`, borderRadius:14, boxShadow:SHADOW_SM }}>
                <div style={{ fontSize:40, fontWeight:900, color:`${b.color}20`, lineHeight:1, marginBottom:18, letterSpacing:"-.04em" }}>{b.n}</div>
                <h3 style={{ fontSize:17, fontWeight:800, color:TEXT, marginBottom:12, lineHeight:1.3 }}>{b.title}</h3>
                <p style={{ fontSize:14, color:TEXT2, lineHeight:1.75 }}>{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* Who it's for */}
      <section style={{ padding:"96px 48px", background:BG }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r3} className="scroll-reveal" style={{ textAlign:"center", marginBottom:48 }}>
            <div className="sec-tag" style={{ marginBottom:14 }}>Who Aspreo Is For</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,44px)", fontWeight:900, letterSpacing:"-.025em" }}>Built for builders</h2>
            <p style={{ fontSize:16, color:TEXT2, maxWidth:480, margin:"12px auto 0", lineHeight:1.7 }}>Whether you're a freelancer, consultant, creator, or running a growing agency — Aspreo fits where you are.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:20 }}>
            {[
              { icon:"💻", who:"Freelancers & Consultants", body:"Send professional invoices, keep a client roster with full history, manage your consultant profile, and track your personal finances — all in one place, without subscribing to five separate tools.", plan:"Free — full access" },
              { icon:"🥗", who:"Health & Fitness Focused", body:"Track meals from a 130+ item Indian food library, log workouts from a 75+ exercise library, monitor body composition, and set real health goals like Fat Loss, Muscle Gain, or Strength.", plan:"Free — full access" },
              { icon:"🧠", who:"Readers, Writers & Thinkers", body:"Build a Second Brain of notes, books, articles, and ideas, organized into domains and collections. Keep a lifelong reflection journal with mood trends and writing streaks.", plan:"Free — full access" },
              { icon:"🌱", who:"Students & Personal Use", body:"Track your goals, build daily habits, manage your personal finances, read more books, and plan your weeks intentionally. Every module included — no credit card, no expiry, no catch.", plan:"Free — full access" },
            ].map(u => (
              <div key={u.who} style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:14, padding:24, display:"flex", gap:18, alignItems:"flex-start" }}>
                <div style={{ fontSize:36, flexShrink:0 }}>{u.icon}</div>
                <div>
                  <div style={{ fontWeight:800, fontSize:16, color:TEXT, marginBottom:8 }}>{u.who}</div>
                  <p style={{ fontSize:14, color:TEXT2, lineHeight:1.7, marginBottom:12 }}>{u.body}</p>
                  <span style={{ fontSize:11, padding:"3px 10px", borderRadius:99, background:PRIMARY+"15", color:PRIMARY, fontWeight:700 }}>{u.plan}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* India-first */}
      <section ref={r4} className="scroll-reveal" style={{ padding:"96px 48px", background:DARK, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 25% 50%, rgba(43,29,201,.4) 0%, transparent 50%)`, pointerEvents:"none" }}/>
        <div style={{ maxWidth:720, margin:"0 auto", textAlign:"center", position:"relative", zIndex:1 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🇮🇳</div>
          <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, color:"#fff", marginBottom:16, letterSpacing:"-.025em" }}>Built for India. Ready for the world.</h2>
          <p style={{ fontSize:17, color:"rgba(255,255,255,.7)", lineHeight:1.8, marginBottom:28 }}>
            Aspreo is built with India in mind — a food library full of real Indian dishes, GST-ready invoices, IST-aware scheduling, and a support team that understands the local context. Free for everyone. Hosted securely.
          </p>
          <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap" }}>
            {[["💳","Razorpay Payments","UPI, cards, net banking, EMI"],["🧾","GST-Ready Invoices","GSTIN, HSN, Indian formats"],["🕐","IST-Aware","Scheduling in your timezone"],["🔐","AES-256","Your data encrypted at rest"]].map(([icon,label,sub]) => (
              <div key={label} style={{ background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.12)", borderRadius:12, padding:"16px 20px", textAlign:"center" }}>
                <div style={{ fontSize:28, marginBottom:6 }}>{icon}</div>
                <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{label}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", marginTop:2 }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}


function VisionPage() {
  const r1=useScrollReveal(), r2=useScrollReveal(), r3=useScrollReveal(), r4=useScrollReveal();
  return (
    <div>
      {/* Hero */}
      <section style={{ padding:"160px 48px 100px", position:"relative", overflow:"hidden", background:BG }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 70% 30%, ${P_LIGHT} 0%, transparent 50%), radial-gradient(circle at 20% 70%, rgba(13,148,136,.06) 0%, transparent 40%)`, pointerEvents:"none" }}/>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle, ${BORDER} 1px, transparent 1px)`, backgroundSize:"20px 20px", opacity:.5, pointerEvents:"none" }}/>
        <div style={{ maxWidth:760, position:"relative", zIndex:1 }} className="fade-up">
          <div className="sec-tag" style={{ marginBottom:22 }}>Our Vision</div>
          <h1 style={{ fontSize:"clamp(40px,6vw,72px)", fontWeight:900, lineHeight:1.06, letterSpacing:"-.035em", marginBottom:24 }}>
            The platform where<br/><em style={{ fontStyle:"italic", color:PRIMARY }}>life meets work.</em>
          </h1>
          <p style={{ fontSize:18, color:TEXT2, lineHeight:1.8, fontWeight:300, maxWidth:640 }}>
            We envision a world where every solopreneur, freelancer, and agency owner has the same operational power as a Fortune 500 company — without the complexity, the team, or the enterprise software price tag.
          </p>
        </div>
      </section>

      <div className="divider"/>

      {/* The vision */}
      <section style={{ padding:"96px 48px" }}>
        <div ref={r1} className="scroll-reveal" style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center" }}>
            <div>
              <div className="sec-tag" style={{ marginBottom:14 }}>Where We're Going</div>
              <h2 style={{ fontSize:"clamp(26px,3.5vw,44px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.15, marginBottom:20 }}>
                A single workspace for the entire human experience of work
              </h2>
              <p style={{ fontSize:15, color:TEXT2, lineHeight:1.8, marginBottom:16 }}>
                Today, Aspreo replaces 8 tools with one. Tomorrow, Aspreo will be the operating system for independent professionals — with AI-powered insights, predictive scheduling, automated client communications, and a unified financial picture that spans personal and business finance.
              </p>
              <p style={{ fontSize:15, color:TEXT2, lineHeight:1.8 }}>
                We believe that the lines between "personal" and "professional" growth are artificial. The solopreneur who wakes up with clear priorities, maintains consistent habits, and has structured systems for every client interaction outperforms everyone. Aspreo is that system.
              </p>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {[
                { year:"Today",  icon:"✅", color:PRIMARY,    title:"All-in-one workspace", body:"CRM, invoices, meetings, payments, social media, and personal productivity unified." },
                { year:"2025",   icon:"🤖", color:"#7C3AED",  title:"AI-powered insights", body:"Intelligent suggestions for follow-ups, optimal meeting times, content ideas, and financial planning." },
                { year:"2026",   icon:"🌐", color:"#0D9488",  title:"Global expansion",     body:"Multi-currency, multi-language, and compliance tools for solopreneurs across Southeast Asia and beyond." },
              ].map(r => (
                <div key={r.year} style={{ display:"flex", gap:16, padding:20, background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:12, boxShadow:SHADOW_SM }}>
                  <div style={{ width:44, height:44, borderRadius:11, background:r.color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{r.icon}</div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:r.color, textTransform:"uppercase", letterSpacing:".08em", marginBottom:3 }}>{r.year}</div>
                    <div style={{ fontWeight:700, fontSize:14, color:TEXT, marginBottom:4 }}>{r.title}</div>
                    <div style={{ fontSize:13, color:TEXT2, lineHeight:1.6 }}>{r.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* The personal + professional integration */}
      <section style={{ padding:"96px 48px", background:SURFACE }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r2} className="scroll-reveal" style={{ textAlign:"center", marginBottom:56 }}>
            <div className="sec-tag" style={{ marginBottom:14 }}>The Integration</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, letterSpacing:"-.025em", lineHeight:1.1, marginBottom:14 }}>
              Why personal and professional<br/>belong in the same place
            </h2>
            <p style={{ fontSize:16, color:TEXT2, maxWidth:560, margin:"0 auto", lineHeight:1.7 }}>
              The most effective solopreneurs don't separate their morning routine from their client work. Their daily habits, their quarterly goals, and their business systems are all part of the same operating system. Aspreo reflects that reality.
            </p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
            {[
              { icon:"🌅", color:AMBER,   title:"Morning: Personal System",  items:["Review today's priorities","Complete daily check-ins","Log gratitude & intentions","Check habit streak — 34 days","Review monthly goal progress"] },
              { icon:"💼", color:PRIMARY, title:"Daytime: Business Operations",items:["Check CRM — 3 new leads","Send 2 proposals from templates","Confirm client booking (auto-email)","Review invoice status — ₹45K pending","Post scheduled LinkedIn content"] },
              { icon:"🌙", color:"#7C3AED",title:"Evening: Review & Automate",items:["Payment received — auto-delivered","Booking confirmation sent","Update weekly planner","Log book progress","Tomorrow's tasks set"] },
            ].map(col => (
              <div key={col.title} style={{ background:BG, border:`1px solid ${col.color}25`, borderTop:`3px solid ${col.color}`, borderRadius:14, padding:24 }}>
                <div style={{ fontSize:28, marginBottom:10 }}>{col.icon}</div>
                <div style={{ fontWeight:800, fontSize:15, color:TEXT, marginBottom:16 }}>{col.title}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {col.items.map((item,i) => (
                    <div key={i} style={{ display:"flex", gap:8, fontSize:13, color:TEXT2, alignItems:"flex-start" }}>
                      <span style={{ color:col.color, fontWeight:700, flexShrink:0 }}>→</span>{item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* Values */}
      <section style={{ padding:"96px 48px", background:BG }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div ref={r3} className="scroll-reveal" style={{ textAlign:"center", marginBottom:48 }}>
            <div className="sec-tag" style={{ marginBottom:14 }}>Our Values</div>
            <h2 style={{ fontSize:"clamp(28px,4vw,44px)", fontWeight:900, letterSpacing:"-.025em" }}>What we build on</h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:20 }}>
            {[
              { icon:"🎯", title:"Intentionality over busyness",   body:"We build tools that help you do fewer things better — not more things faster. Every feature earns its place by removing friction from something that matters." },
              { icon:"🔐", title:"Privacy by design",               body:"Your goals, your finances, your client data — these are sacred. Aspreo uses AES-256 encryption at rest and never sells your data. Full stop." },
              { icon:"🇮🇳", title:"India-first, globally ready",    body:"Built with Indian business context at the core — GST invoicing, Razorpay, INR pricing, IST scheduling — while being architected for global scale." },
              { icon:"⚡", title:"Automation that respects you",   body:"We automate the repetitive (delivery emails, booking confirmations, payment receipts) while keeping you in control of everything that requires human judgment." },
            ].map(v => (
              <div key={v.title} style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:14, padding:28, display:"flex", gap:18 }}>
                <div style={{ fontSize:36, flexShrink:0 }}>{v.icon}</div>
                <div>
                  <div style={{ fontWeight:800, fontSize:16, color:TEXT, marginBottom:8 }}>{v.title}</div>
                  <p style={{ fontSize:14, color:TEXT2, lineHeight:1.75, margin:0 }}>{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* CTA */}
      <section ref={r4} className="scroll-reveal" style={{ padding:"120px 48px", background:DARK, textAlign:"center", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 50% 50%, rgba(43,29,201,.5) 0%, transparent 60%)`, pointerEvents:"none" }}/>
        <div style={{ maxWidth:640, margin:"0 auto", position:"relative", zIndex:1 }}>
          <h2 style={{ fontSize:"clamp(32px,5vw,60px)", fontWeight:900, color:"#fff", marginBottom:16, letterSpacing:"-.03em", lineHeight:1.08 }}>
            The vision starts with you.
          </h2>
          <p style={{ fontSize:17, color:"rgba(255,255,255,.7)", marginBottom:36, lineHeight:1.7 }}>
            Join everyone building their life system on Aspreo — goals, knowledge, fitness, and reflection, all in one place. Free for every account. Powerful from day one.
          </p>
          <button className="s-btn s-btn-primary" style={{ fontSize:16, padding:"15px 40px" }} onClick={()=>window.location.href="/"}>
            Start Building for Free →
          </button>
          <div style={{ marginTop:20, fontSize:13, color:"rgba(255,255,255,.4)" }}>No credit card required</div>
        </div>
      </section>
    </div>
  );
}


function PricingPageEmbed({ onLogin }) {
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState("monthly");
  const PLAN_COLORS = { hobby:"#64748B", solopreneur:"#7C3AED", agency:"#2B1DC9" };
  const PLAN_ICONS  = { hobby:"🌱", solopreneur:"⚡", agency:"🏢" };

  useEffect(() => {
    import("../lib/supabase").then(({ supabase }) => {
      supabase.from("plans").select("*").eq("is_active",true).order("sort_order")
        .then(({ data }) => { setPlans(data||[]); setLoading(false); });
    });
  }, []);

  const getPrice = (plan) => {
    if (plan.offer_active && plan.offer_price) return plan.offer_price;
    return billing==="yearly" ? (plan.price_yearly||plan.price_monthly*10) : plan.price_monthly;
  };

  return (
    <div style={{paddingTop:80,paddingBottom:80,minHeight:"100vh",background:BG,fontFamily:FONT}}>
      <div style={{maxWidth:980,margin:"0 auto",padding:"0 24px"}}>
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{fontSize:12,fontWeight:700,color:PRIMARY,textTransform:"uppercase",letterSpacing:".1em",marginBottom:10}}>Pricing</div>
          <h1 style={{fontSize:40,fontWeight:900,color:TEXT,margin:"0 0 12px",lineHeight:1.1}}>Simple pricing. No surprises.</h1>
          <p style={{fontSize:16,color:TEXT2,maxWidth:420,margin:"0 auto 24px",lineHeight:1.6}}>Start free. Upgrade when you're ready. Cancel anytime.</p>
          {plans.some(p=>p.offer_active&&p.offer_text)&&(
            <div style={{display:"inline-block",background:"#FAEEDA",border:"1px solid #D97706",borderRadius:10,padding:"10px 20px",marginBottom:20,fontSize:13,color:"#92400E",fontWeight:600}}>
              🏷 {plans.find(p=>p.offer_active&&p.offer_text)?.offer_text}
            </div>
          )}
          <div style={{display:"inline-flex",background:"#E8E8F0",borderRadius:10,padding:4,gap:4}}>
            {["monthly","yearly"].map(b=>(
              <button key={b} onClick={()=>setBilling(b)}
                style={{background:billing===b?"#fff":"transparent",color:billing===b?PRIMARY:TEXT3,border:"none",borderRadius:7,padding:"8px 22px",cursor:"pointer",fontFamily:FONT,fontSize:13,fontWeight:billing===b?700:400,boxShadow:billing===b?"0 1px 4px rgba(0,0,0,.1)":"none",transition:"all .15s"}}>
                {b==="monthly"?"Monthly":"Yearly"}
                {b==="yearly"&&<span style={{marginLeft:6,fontSize:10,background:"#DCFCE7",color:GREEN,padding:"2px 7px",borderRadius:99,fontWeight:700}}>Save ~17%</span>}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div style={{textAlign:"center",padding:60,color:TEXT3}}>Loading plans…</div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:20,marginBottom:48}}>
            {plans.map(plan=>{
              const price    = getPrice(plan);
              const color    = PLAN_COLORS[plan.id]||PRIMARY;
              const icon     = PLAN_ICONS[plan.id]||"⭐";
              const features = typeof plan.features==="string"?JSON.parse(plan.features):(plan.features||[]);
              return (
                <div key={plan.id} style={{background:SURFACE,border:`2px solid ${plan.is_featured?color:BORDER}`,borderRadius:18,padding:"28px 24px",position:"relative",boxShadow:plan.is_featured?`0 8px 40px ${color}18`:""}}>
                  {plan.is_featured&&<div style={{position:"absolute",top:-14,left:"50%",transform:"translateX(-50%)",background:color,color:"#fff",fontSize:11,fontWeight:700,padding:"4px 16px",borderRadius:99,whiteSpace:"nowrap"}}>Most Popular</div>}
                  <div style={{fontSize:32,marginBottom:8}}>{icon}</div>
                  <div style={{fontSize:12,fontWeight:700,color,textTransform:"uppercase",letterSpacing:".1em",marginBottom:4}}>{plan.name}</div>
                  <div style={{fontSize:13,color:TEXT3,marginBottom:20,lineHeight:1.5}}>{plan.tagline}</div>
                  <div style={{marginBottom:24}}>
                    {price===0
                      ?<><div style={{fontSize:40,fontWeight:900,color:TEXT}}>Free</div><div style={{fontSize:13,color:TEXT3}}>forever</div></>
                      :<><div style={{display:"flex",alignItems:"baseline",gap:2}}><span style={{fontSize:18,color:TEXT}}>₹</span><span style={{fontSize:40,fontWeight:900,color}}>{price}</span><span style={{fontSize:13,color:TEXT3}}>/mo</span></div>{billing==="yearly"&&<div style={{fontSize:12,color:GREEN,fontWeight:600}}>Billed ₹{price*12}/yr</div>}</>
                    }
                  </div>
                  <button onClick={onLogin} style={{display:"block",width:"100%",textAlign:"center",padding:"12px",background:plan.is_featured?color:"transparent",color:plan.is_featured?"#fff":color,border:`2px solid ${color}`,borderRadius:10,fontWeight:700,fontSize:14,marginBottom:24,cursor:"pointer",fontFamily:FONT}}>
                    {price===0?"Start Free — No Card Needed →":`Get ${plan.name} →`}
                  </button>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {features.slice(0,8).map((f,i)=>(
                      <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",fontSize:13,color:TEXT}}>
                        <span style={{color:GREEN,fontWeight:700,flexShrink:0}}>✓</span>{f}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{textAlign:"center",background:`linear-gradient(135deg,${PRIMARY},#4F46E5)`,borderRadius:20,padding:"48px 32px",color:"#fff"}}>
          <h2 style={{fontSize:26,fontWeight:900,margin:"0 0 10px"}}>Start building your life system today</h2>
          <p style={{fontSize:15,opacity:.8,margin:"0 0 24px"}}>Free forever. No credit card required.</p>
          <button onClick={onLogin} style={{display:"inline-block",background:"#fff",color:PRIMARY,padding:"14px 36px",borderRadius:10,fontWeight:800,fontSize:15,border:"none",cursor:"pointer",fontFamily:FONT}}>Get Started Free →</button>
        </div>
      </div>
    </div>
  );
}

function Footer({ setPage }) {
  return (
    <footer style={{ background:DARK, padding:"56px 48px 36px", color:"rgba(255,255,255,.35)", fontSize:13 }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:44, flexWrap:"wrap", gap:28 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:12 }}>
              <div style={{ width:30, height:30, background:PRIMARY, borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <span style={{ fontSize:12, fontWeight:900, color:"#fff", fontFamily:"sans-serif" }}>A</span>
              </div>
              <span style={{ fontFamily:FONT, fontSize:16, fontWeight:800, color:"#fff", letterSpacing:"-.02em" }}>Aspreo</span>
            </div>
            <p style={{ fontSize:13, lineHeight:1.7, maxWidth:260 }}>Your business and life OS — CRM, invoices, meetings, payments, social media, and personal productivity in one platform.</p>
            <div style={{ marginTop:14, display:"flex", gap:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:10, padding:"3px 10px", borderRadius:20, background:`${PRIMARY}25`, color:"#818cf8", border:`1px solid ${PRIMARY}30` }}>🔒 AES-256 Encrypted</span>
              <span style={{ fontSize:10, padding:"3px 10px", borderRadius:20, background:`${GREEN}18`, color:"#4ade80", border:`1px solid ${GREEN}25` }}>🇮🇳 Built for India</span>
            </div>
          </div>
          <div style={{ display:"flex", gap:44 }}>
            {[
              ["Product",  [["home","Home"],["about","About"],["vision","Vision"]]],
              ["Company",  [["about","About Us"],["vision","Our Vision"]]],
            ].map(([title,links]) => (
              <div key={title}>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:"rgba(255,255,255,.2)", marginBottom:14 }}>{title}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {links.map(([id,label]) => (
                    <button key={id} onClick={()=>setPage(id)} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,.4)", fontSize:13, textAlign:"left", fontFamily:FONT, padding:0, transition:"color .15s" }}
                      onMouseEnter={e=>e.target.style.color="#fff"} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.4)"}>{label}</button>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:"rgba(255,255,255,.2)", marginBottom:14 }}>Contact</div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <a href="mailto:support@aspreo.com" style={{ color:"rgba(255,255,255,.4)", fontSize:13, textDecoration:"none" }}>support@aspreo.com</a>
                
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:"rgba(255,255,255,.2)", marginBottom:14 }}>Legal</div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <a href="/privacy" style={{ color:"rgba(255,255,255,.4)", fontSize:13, textDecoration:"none" }}
                  onMouseEnter={e=>e.target.style.color='#fff'} onMouseLeave={e=>e.target.style.color='rgba(255,255,255,.4)'}>Privacy Policy</a>
                <a href="/terms" style={{ color:"rgba(255,255,255,.4)", fontSize:13, textDecoration:"none" }}
                  onMouseEnter={e=>e.target.style.color='#fff'} onMouseLeave={e=>e.target.style.color='rgba(255,255,255,.4)'}>Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
        <div style={{ height:1, background:"rgba(255,255,255,.07)" }}/>
        <div style={{ paddingTop:22, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
          <span>© 2026 Aspreo. All rights reserved.</span>
          <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
            <a href="/privacy" style={{ color:'rgba(255,255,255,.35)', textDecoration:'none', fontSize:13 }}
              onMouseEnter={e=>e.target.style.color='#fff'} onMouseLeave={e=>e.target.style.color='rgba(255,255,255,.35)'}>Privacy Policy</a>
            <a href="/terms" style={{ color:'rgba(255,255,255,.35)', textDecoration:'none', fontSize:13 }}
              onMouseEnter={e=>e.target.style.color='#fff'} onMouseLeave={e=>e.target.style.color='rgba(255,255,255,.35)'}>Terms of Service</a>
            <span style={{ color:'rgba(255,255,255,.35)' }}>Free for every account</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function AspreoSite({ onLogin, onWaitlist }) {
  const [page, setPage] = useState("home");
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);
  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: BG, fontFamily: FONT }}>
        <Nav page={page} setPage={setPage} onLogin={onLogin} onWaitlist={onWaitlist}/>
        <main style={{ flex: 1, paddingTop: 0 }}>
          {page === "home"     && <HomePage onLogin={onLogin} onWaitlist={onWaitlist}/>}
          {page === "about"    && <AboutPage/>}
          {page === "vision"   && <VisionPage/>}


        </main>
        <Footer setPage={setPage}/>
      </div>
    </>
  );
}
